/**
 * 当前选中的角色。
 *
 * 现在只有缈缈一个角色，但数据模型从一开始就是多角色的
 * （`Relationship` 的唯一键是 user × character），管理端不能假设只有一个。
 * 先把切换器做出来，加第二个角色时不需要回头改每一个页面。
 *
 * 选择存在 localStorage，刷新后保持——排障时经常开一堆标签页，
 * 每次都要重选角色很烦。
 *
 * **切换角色只影响筛选，不影响权限。** 服务端不会因为你选了某个角色
 * 就放宽鉴权；每个接口仍然独立校验 permission。
 */

import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "./api";
import { useAuth } from "./auth";

const STORAGE_KEY = "dreamverse.admin.characterId";

export interface CharacterOption {
  id: string;
  key: string;
  displayName: string;
  status: string;
}

interface CharacterState {
  characters: CharacterOption[];
  /** 当前选中的角色 ID。`null` 表示「全部角色」。 */
  characterId: string | null;
  setCharacterId: (id: string | null) => void;
  loading: boolean;
}

const CharacterContext = createContext<CharacterState | null>(null);

export function CharacterProvider({ children }: { children: ReactNode }) {
  const { admin, can } = useAuth();
  const [characterId, setState] = useState<string | null>(() => localStorage.getItem(STORAGE_KEY));

  const { data, isLoading } = useQuery({
    queryKey: ["characters", "switcher"],
    // 没有 character.read 权限就不请求，避免整页因为 403 报错
    enabled: !!admin && can("character.read"),
    queryFn: () => api.get<CharacterOption[]>("/api/v1/admin/characters"),
    staleTime: 5 * 60_000,
  });

  const characters = useMemo(() => data ?? [], [data]);

  // 首次加载默认选中第一个角色。只有一个角色时「全部」和「它」等价，
  // 直接选中它，界面上更明确。
  useEffect(() => {
    if (characters.length === 0) return;
    const valid = characterId && characters.some((c) => c.id === characterId);
    if (!valid) {
      const next = characters[0]?.id ?? null;
      setState(next);
      if (next) localStorage.setItem(STORAGE_KEY, next);
    }
  }, [characters, characterId]);

  const setCharacterId = (id: string | null) => {
    setState(id);
    if (id) localStorage.setItem(STORAGE_KEY, id);
    else localStorage.removeItem(STORAGE_KEY);
  };

  const value = useMemo<CharacterState>(
    () => ({ characters, characterId, setCharacterId, loading: isLoading }),
    [characters, characterId, isLoading],
  );

  return <CharacterContext.Provider value={value}>{children}</CharacterContext.Provider>;
}

export function useCharacter(): CharacterState {
  const ctx = useContext(CharacterContext);
  if (!ctx) throw new Error("useCharacter 必须在 CharacterProvider 内使用");
  return ctx;
}

/**
 * 给按角色筛选的列表页用：直接展开进 `fixedParams`。
 * 选了「全部角色」时返回空对象，不带这个参数。
 */
export function useCharacterScope(): { characterId?: string } {
  const { characterId } = useCharacter();
  return characterId ? { characterId } : {};
}
